/**
 * 智能体团队配置状态管理 Store
 *
 * 使用 zustand 管理规划者和监督者的配置状态，独立于会话状态。
 */

import { create } from 'zustand';
import { getAgentTeamApi } from '../services/agentTeamApi';
import { sanitizeErrorMessage } from '../utils/validation';
import type {
  RoleConfig,
  ConfigUpdateRequest,
  ConfigResetRequest,
  ConfigListResponse,
  LLMConfig,
} from '../types/agentTeam';

interface ConfigFormState {
  display_name: string;
  prompt: string;
  enabled_tools: string[];
  temperature: number;
  llm_config: LLMConfig | null;
}

interface AgentTeamConfigState {
  plannerConfig: RoleConfig | null;
  supervisorConfig: RoleConfig | null;
  workspaceId: number | null;
  loading: boolean;
  saving: boolean;
  error: string | null;

  plannerForm: ConfigFormState;
  supervisorForm: ConfigFormState;
  formErrors: Map<string, string>;

  getPlannerConfig: () => RoleConfig | null;
  getSupervisorConfig: () => RoleConfig | null;
  getPlannerDisplayName: () => string;
  getSupervisorDisplayName: () => string;
  getFormState: (role: 'planner' | 'supervisor') => ConfigFormState;
  getFormError: (field: string) => string | undefined;

  loadConfig: (workspaceId: number) => Promise<void>;
  updateConfig: (role: 'planner' | 'supervisor', updates: Partial<ConfigFormState>) => void;
  saveConfig: (role: 'planner' | 'supervisor') => Promise<void>;
  resetConfig: (role?: 'planner' | 'supervisor') => Promise<void>;
  validateForm: (role: 'planner' | 'supervisor') => boolean;
  setFormError: (field: string, error: string | undefined) => void;
  clearFormErrors: () => void;

  setError: (error: string | null) => void;
  clearError: () => void;
  cleanup: () => void;
}

const DEFAULT_FORM_STATE: ConfigFormState = {
  display_name: '',
  prompt: '',
  enabled_tools: [],
  temperature: 0.7,
  llm_config: null,
};

const validateDisplayName = (name: string): string | undefined => {
  if (!name.trim()) {
    return '显示名称不能为空';
  }
  if (name.length > 20) {
    return '显示名称不能超过20个字符';
  }
  return undefined;
};

const validatePrompt = (prompt: string): string | undefined => {
  if (!prompt.trim()) {
    return '提示词不能为空';
  }
  if (prompt.length > 5000) {
    return '提示词不能超过5000个字符';
  }
  return undefined;
};

const validateTemperature = (temp: number): string | undefined => {
  if (temp < 0 || temp > 2) {
    return '温度必须在0.0到2.0之间';
  }
  return undefined;
};

export const useAgentTeamConfigStore = create<AgentTeamConfigState>()((set, get) => ({
  plannerConfig: null,
  supervisorConfig: null,
  workspaceId: null,
  loading: false,
  saving: false,
  error: null,
  plannerForm: DEFAULT_FORM_STATE,
  supervisorForm: DEFAULT_FORM_STATE,
  formErrors: new Map(),

  getPlannerConfig: () => get().plannerConfig,
  getSupervisorConfig: () => get().supervisorConfig,
  getPlannerDisplayName: () => get().plannerConfig?.display_name || '规划者',
  getSupervisorDisplayName: () => get().supervisorConfig?.display_name || '监督者',
  getFormState: (role: 'planner' | 'supervisor') =>
    role === 'planner' ? get().plannerForm : get().supervisorForm,
  getFormError: (field: string) => get().formErrors.get(field),

  loadConfig: async (workspaceId: number) => {
    const state = get();
    if (state.loading) return;

    set({ loading: true, error: null, workspaceId });

    try {
      const api = getAgentTeamApi();
      const response: ConfigListResponse = await api.getConfig(workspaceId);

      const plannerForm: ConfigFormState = response.planner
        ? {
            display_name: response.planner.display_name,
            prompt: response.planner.prompt,
            enabled_tools: response.planner.enabled_tools,
            temperature: response.planner.temperature,
            llm_config: response.planner.llm_config,
          }
        : DEFAULT_FORM_STATE;

      const supervisorForm: ConfigFormState = response.supervisor
        ? {
            display_name: response.supervisor.display_name,
            prompt: response.supervisor.prompt,
            enabled_tools: response.supervisor.enabled_tools,
            temperature: response.supervisor.temperature,
            llm_config: response.supervisor.llm_config,
          }
        : DEFAULT_FORM_STATE;

      set({
        plannerConfig: response.planner,
        supervisorConfig: response.supervisor,
        plannerForm,
        supervisorForm,
        loading: false,
      });
    } catch (error) {
      const errorMessage = sanitizeErrorMessage(error, '加载配置失败');
      set({ error: errorMessage, loading: false });
      throw error;
    }
  },

  updateConfig: (role: 'planner' | 'supervisor', updates: Partial<ConfigFormState>) => {
    set(state => {
      if (role === 'planner') {
        return {
          plannerForm: { ...state.plannerForm, ...updates },
        };
      } else {
        return {
          supervisorForm: { ...state.supervisorForm, ...updates },
        };
      }
    });
  },

  saveConfig: async (role: 'planner' | 'supervisor') => {
    const state = get();
    if (state.saving) return;

    const isValid = get().validateForm(role);
    if (!isValid) {
      return;
    }

    set({ saving: true, error: null });

    try {
      const api = getAgentTeamApi();
      const formState = role === 'planner' ? state.plannerForm : state.supervisorForm;

      const request: ConfigUpdateRequest = {
        role,
        display_name: formState.display_name,
        prompt: formState.prompt,
        enabled_tools: formState.enabled_tools,
        temperature: formState.temperature,
        llm_config: formState.llm_config || undefined,
      };

      const savedConfig = await api.updateConfig(state.workspaceId!, request);

      set(state => ({
        plannerConfig: role === 'planner' ? savedConfig : state.plannerConfig,
        supervisorConfig: role === 'supervisor' ? savedConfig : state.supervisorConfig,
        saving: false,
      }));
    } catch (error) {
      const errorMessage = sanitizeErrorMessage(error, '保存配置失败');
      set({ error: errorMessage, saving: false });
      throw error;
    }
  },

  resetConfig: async (role?: 'planner' | 'supervisor') => {
    const state = get();
    if (state.loading) return;

    set({ loading: true, error: null });

    try {
      const api = getAgentTeamApi();
      const request: ConfigResetRequest = role ? { role } : {};
      const response = await api.resetConfig(state.workspaceId!, request);

      const plannerForm: ConfigFormState = response.planner
        ? {
            display_name: response.planner.display_name,
            prompt: response.planner.prompt,
            enabled_tools: response.planner.enabled_tools,
            temperature: response.planner.temperature,
            llm_config: response.planner.llm_config,
          }
        : DEFAULT_FORM_STATE;

      const supervisorForm: ConfigFormState = response.supervisor
        ? {
            display_name: response.supervisor.display_name,
            prompt: response.supervisor.prompt,
            enabled_tools: response.supervisor.enabled_tools,
            temperature: response.supervisor.temperature,
            llm_config: response.supervisor.llm_config,
          }
        : DEFAULT_FORM_STATE;

      set({
        plannerConfig: response.planner,
        supervisorConfig: response.supervisor,
        plannerForm,
        supervisorForm,
        formErrors: new Map(),
        loading: false,
      });
    } catch (error) {
      const errorMessage = sanitizeErrorMessage(error, '重置配置失败');
      set({ error: errorMessage, loading: false });
      throw error;
    }
  },

  validateForm: (role: 'planner' | 'supervisor') => {
    const formState = role === 'planner' ? get().plannerForm : get().supervisorForm;
    const errors = new Map<string, string>();

    const displayNameError = validateDisplayName(formState.display_name);
    if (displayNameError) errors.set('display_name', displayNameError);

    const promptError = validatePrompt(formState.prompt);
    if (promptError) errors.set('prompt', promptError);

    const temperatureError = validateTemperature(formState.temperature);
    if (temperatureError) errors.set('temperature', temperatureError);

    set({ formErrors: errors });

    return errors.size === 0;
  },

  setFormError: (field: string, error: string | undefined) => {
    set(state => {
      const errors = new Map(state.formErrors);
      if (error) {
        errors.set(field, error);
      } else {
        errors.delete(field);
      }
      return { formErrors: errors };
    });
  },

  clearFormErrors: () => {
    set({ formErrors: new Map() });
  },

  setError: (error: string | null) => {
    set({ error });
  },

  clearError: () => {
    set({ error: null });
  },

  cleanup: () => {
    set({
      plannerConfig: null,
      supervisorConfig: null,
      workspaceId: null,
      loading: false,
      saving: false,
      error: null,
      plannerForm: DEFAULT_FORM_STATE,
      supervisorForm: DEFAULT_FORM_STATE,
      formErrors: new Map(),
    });
  },
}));

export default useAgentTeamConfigStore;